<template>
  <main class="page no-tabs">
    <page-header title="客户详情" back @back="back">
      <button v-if="info" class="text-button" type="button" @click="follow">编辑资料</button>
    </page-header>
    <div v-if="loading" class="loading-state"><span class="spinner" /> 正在读取客户资料</div>
    <div v-if="error" class="error-message" role="alert">
      {{ error }}
      <button class="text-button" @click="load">重试</button>
    </div>
    <template v-if="info">
      <div class="d-chips">
        <span v-if="info.labels_text" class="tag brand">{{ info.labels_text }}级</span>
        <span v-if="info.status_text" class="tag soft">{{ info.status_text }}</span>
        <span v-if="channel" class="tag">{{ channel }}</span>
        <span v-if="info.created_at" class="tag">报备 {{ shortDate(info.created_at) }}</span>
      </div>
      <h1 class="d-title">{{ need }}</h1>
      <p class="d-who">
        <strong>{{ info.name || '未填写姓名' }}</strong>
        <a v-if="callable" class="nowrap" :href="'tel:' + info.mobile">{{ phoneText }}</a>
        <span v-else>{{ info.mobile || '联系方式受权限保护' }}</span>
      </p>

      <section class="d-next">
        <button type="button" @click="follow">
          <small>下次跟进</small>
          <strong :class="{ late: isDue, empty: !info.log_time }">{{ nextText }}</strong>
        </button>
        <div>
          <small>预计来访</small>
          <strong :class="{ empty: !info.come_time }">{{ comeText }}</strong>
        </div>
      </section>

      <section class="d-facts">
        <div v-for="row in shownFacts" :key="row.label" :class="{ wide: row.wide }">
          <small>{{ row.label }}</small>
          <span>{{ row.value }}</span>
        </div>
        <div v-if="!facts.length" class="wide">
          <small>客户资料</small>
          <span>还没有补充需求和背景</span>
        </div>
        <div class="d-facts-foot">
          <button v-if="facts.length > factLimit" type="button" @click="factLimit = facts.length">
            展开全部 {{ facts.length }} 项
          </button>
          <span v-else>{{ missing ? '还有 ' + missing + ' 项资料未填' : '资料已填写完整' }}</span>
          <button type="button" @click="follow">{{ missing ? '去完善' : '修改' }}</button>
        </div>
      </section>

      <customer-ai-assistant :key="info.id" :customer-id="info.id" :profile-values="info" detail @schedule="scheduleAi" @profile="profileAi" />

      <div class="sec-head">
        <h2>跟进记录 {{ records.length }}</h2>
        <span>负责人 {{ info.employee_text || '待分配' }}</span>
      </div>
      <section class="timeline">
        <p v-if="!records.length" class="d-empty">还没有跟进记录，联系后记下需求和下一步安排。</p>
        <article v-for="(log, index) in visibleLogs" :key="log.id" class="log" :class="{ first: index === 0, mini: !clean(log.content) }">
          <div class="log-head">
            <span><b>{{ timeText(log.created_at) }}</b>{{ logMeta(log) }}</span>
            <span v-if="isBest(log)" class="star">重点</span>
            <button v-else-if="clean(log.content) && log.id !== 'latest'" class="mark" type="button" :disabled="marking" @click="mark(log)">设为重点</button>
          </div>
          <p v-if="clean(log.content)" :class="{ collapsed: isLong(log.content) && !expandedLogs[log.id] }" v-text="clean(log.content)" />
          <button v-if="isLong(log.content)" class="log-expand" type="button" :aria-expanded="!!expandedLogs[log.id]" @click="$set(expandedLogs, log.id, !expandedLogs[log.id])">
            {{ expandedLogs[log.id] ? '收起' : '展开全文' }}
          </button>
        </article>
        <button v-if="records.length > logLimit" class="log-more" type="button" @click="logLimit += 10">
          再显示 {{ Math.min(records.length - logLimit, 10) }} 条记录
        </button>
      </section>

      <div class="dock">
        <div class="dock-inner">
          <a v-if="callable" class="button soft" :href="'tel:' + info.mobile"><app-icon name="phone" />拨打</a>
          <button v-else class="button soft" type="button" disabled>号码受保护</button>
          <button class="button" type="button" @click="follow"><app-icon name="edit" />写跟进</button>
        </div>
      </div>
    </template>
  </main>
</template>
<script>
import { canCall } from '../../common/customer'
import { scheduleLocation } from '../../common/schedule'
import { clock, dateTimeText, daysFromToday, needText, projectText } from '../../common/when'
import CustomerAiAssistant from '../../components/customer-ai-assistant'
import { validAiTime } from '../../common/ai'
import { profileFields } from '../../common/ai-profile'
const demands = [
  { key: 'total_budget_range_text', label: '总价预算范围' },
  { key: 'price_acceptance_level_text', label: '单价接受程度' },
  { key: 'purpose_v2_text', label: '用途' },
  { key: 'type_text', label: '意向类别' },
  { key: 'region_text', label: '意向区域' },
  { key: 'area_text', label: '意向面积' },
  { key: 'total_text', label: '意向总价' },
  { key: 'price_text', label: '意向单价' },
  { key: 'buy_type_text', label: '购买用途' }
]
const extras = [
  { key: 'income_text', label: '年收入' },
  { key: 'manager_text', label: '决策权' },
  { key: 'industry_text', label: '行业' },
  { key: 'job_text', label: '工作类型' },
  { key: 'job_time_text', label: '工作年限' },
  { key: 'buy_house_text', label: '购房经验' },
  { key: 'financial_text', label: '理财方式' },
  { key: 'introduce_text', label: '转介绍情况' }
]
const wechat = [
  { key: 'wechat', label: '微信号' },
  { key: 'wechat_nickname', label: '微信昵称' },
  { key: 'wechat_status_text', label: '添加情况' },
  { key: 'wechat_add_time', label: '添加时间' }
]
// 编辑页能填写的资料项，用来算“还有几项未填”
const editable = demands.concat(extras, wechat.slice(0, 2))
export default {
  components: { CustomerAiAssistant },
  data() {
    return {
      info: null,
      logs: [],
      subjects: [],
      visits: [],
      successes: [],
      loading: false,
      error: '',
      marking: false,
      logLimit: 10,
      factLimit: 6,
      expandedLogs: {}
    }
  },
  computed: {
    callable() {
      return this.info && canCall(this.info.mobile)
    },
    isDue() {
      const days = this.info && daysFromToday(this.info.log_time)
      return days !== null && days <= 0
    },
    need() {
      return needText(this.info)
    },
    channel() {
      const text = this.clean(this.info.source_channel_v2_text)
      return text && text !== '不限' ? text : ''
    },
    phoneText() {
      const text = String(this.info.mobile || '')
      return /^\d{11}$/.test(text)
        ? text.slice(0, 3) + ' ' + text.slice(3, 7) + ' ' + text.slice(7)
        : text
    },
    nextText() {
      const value = this.info.log_time
      if (!value) return '还未安排'
      const days = daysFromToday(value)
      if (days < 0) return dateTimeText(value, false) + ' · 逾期 ' + -days + ' 天'
      if (days === 0) return '今天' + (clock(value) ? ' ' + clock(value) : '')
      return dateTimeText(value)
    },
    comeText() {
      return this.info.come_time ? dateTimeText(this.info.come_time) : '未约'
    },
    records() {
      if (this.logs.length) return this.logs
      return this.info && this.clean(this.info.log_content)
        ? [
            {
              id: 'latest',
              content: this.info.log_content,
              employee_text: this.info.employee_text,
              created_at: this.info.save_time
            }
          ]
        : []
    },
    visibleLogs() {
      return this.records.slice(0, this.logLimit)
    },
    facts() {
      const subjects = this.subjects
        .map(item => item.name)
        .filter(Boolean)
        .join('、')
      const rows = this.rows([
        { label: '主推项目', value: projectText(this.info.subject_text) },
        { label: '意向项目', value: subjects, wide: subjects.length > 12 }
      ])
        .concat(this.fieldRows(demands), this.fieldRows(wechat), this.fieldRows(extras))
        .concat(
          this.visits.map(visit => ({
            label: '来访时间',
            value: this.timeText(visit.come_time || visit.created_at)
          })),
          this.successes.map(item => ({
            label: '成交时间',
            value: this.timeText(item.success_time || item.created_at)
          }))
        )
      return rows
    },
    shownFacts() {
      return this.facts.slice(0, this.factLimit)
    },
    missing() {
      return editable.filter(field => !this.filled(this.info[field.key])).length
    }
  },
  created() {
    this.load()
  },
  methods: {
    clean(value) {
      return value == null ? '' : String(value).trim()
    },
    filled(value) {
      const text = this.clean(value)
      return !!text && ['不限', '未填写', '未设置', '0'].indexOf(text) < 0
    },
    shortDate(value) {
      return dateTimeText(String(value).slice(0, 10), false)
    },
    timeText(value) {
      const text = this.clean(value)
      return /^\d{4}-\d{2}-\d{2}/.test(text) ? dateTimeText(text) : text || '时间未记录'
    },
    logMeta(log) {
      const parts = []
      if (!this.clean(log.content))
        parts.push(log.type_text ? '更新状态：' + log.type_text : '只更新了状态')
      else if (log.type_text) parts.push(log.type_text)
      if (log.employee_text) parts.push(log.employee_text)
      return parts.map(text => ' · ' + text).join('')
    },
    isBest(log) {
      return log.best === true || log.best === 'True' || log.best === 1 || log.best === '1'
    },
    isLong(value) {
      const text = this.clean(value)
      return text.length > 110 || text.split('\n').length > 4
    },
    rows(items) {
      return items
        .filter(row => this.filled(row.value))
        .map(row => ({ label: row.label, value: this.clean(row.value), wide: !!row.wide }))
    },
    fieldRows(fields) {
      return this.rows(fields.map(field => ({ label: field.label, value: this.info[field.key] })))
    },
    back() {
      if (window.history.length > 1) this.$router.back()
      else
        this.$router.replace(
          this.$route.params.from === 'schedule'
            ? scheduleLocation(this.$route.query.date, this.$route.query.type)
            : this.$route.params.from === 'daily-brief' ? { path: '/daily-brief', query: { date: this.$route.query.date } }
              : this.$route.params.from === 'remind' ? '/remind' : '/customer'
        )
    },
    async load() {
      this.loading = true
      this.error = ''
      try {
        const data = await this.$api.post('/api/customer/detail', { id: this.$route.params.id })
        this.info = data.info
        this.logs = Array.isArray(data.logs) ? data.logs : []
        this.subjects = Array.isArray(data.subjects) ? data.subjects : []
        this.visits = Array.isArray(data.come)
          ? data.come
          : data.come && typeof data.come === 'object' ? [data.come] : []
        this.successes = Array.isArray(data.successes) ? data.successes : []
      } catch (e) {
        this.error = e.message
      } finally {
        this.loading = false
      }
    },
    scheduleAi(value) {
      if (!value) { this.follow(null, true); return }
      const time = validAiTime(value)
      if (time) this.follow(time)
      else this.$toast('建议时间已过，请重新确认')
    },
    profileAi(data) {
      const fields = data.updates.map(update => update.field).filter(field => profileFields.indexOf(field) >= 0)
      if (data.analysisId && fields.length) this.follow(null, false, { ai_profile: data.analysisId, ai_fields: fields.join(',') })
    },
    follow(aiTime, aiPlan, aiProfileQuery) {
      const from =
        ['workbench', 'remind', 'random', 'schedule', 'daily-brief'].indexOf(this.$route.params.from) >= 0
          ? this.$route.params.from
          : 'customer'
      const query = from === 'schedule' ? { date: this.$route.query.date, type: this.$route.query.type } : {}
      if (from === 'daily-brief') query.date = this.$route.query.date
      if (typeof aiTime === 'string' && validAiTime(aiTime)) query.ai_time = aiTime
      if (aiPlan === true) query.ai_plan = '1'
      if (aiProfileQuery) Object.assign(query, aiProfileQuery)
      this.$router.push({
        path: '/customer/edit/' + this.info.id + '/' + from,
        query
      })
    },
    async mark(log) {
      if (this.marking) return
      this.marking = true
      try {
        await this.$dialog.confirm({
          title: '设为重点跟进',
          message: '将这条记录设为该客户的重点跟进？'
        })
        const saved = await this.$api.post('/api/customer/set-best', { id: log.id })
        this.logs.forEach(item => {
          item.best = item.id === log.id
        })
        if (saved.info) this.info = saved.info
        this.$store.commit('customerChanged', { id: this.info.id, info: saved.info, kind: 'best' })
        this.$toast('已标记重点跟进')
      } catch (e) {
        if (e && e.message) this.$toast(e.message)
      } finally {
        this.marking = false
      }
    }
  }
}
</script>



// WEBPACK FOOTER //
// src/pages/customer/detail.vue